/**
 * Timesheet data service — async dispatcher.
 *
 * Routes reads/writes to either the in-memory mock store (default) or the
 * Supabase service based on the same VITE_DATA_SOURCE / app-mode logic the
 * rest of moonshot uses (services/dataService.ts). All public methods are
 * Promise-returning so hooks can `await` them naturally.
 *
 * Lookups for employees / contracts / charge codes / labor categories stay
 * on the mock data even in supabase mode — wiring those to Tesseract's
 * `public.users`, `master_contracts ⋈ task_orders ⋈ work_authorizations`,
 * and `charge_lines` tables is the next BACKLOG sub-task.
 */
import type {
  TsContract,
  TsChargeCode,
  TsEmployee,
  TsTimeEntry,
  TsLaborCategory,
  TsSubmission,
  TsWorkflowEvent,
} from '../types/timesheet.types'
import {
  employees as seedEmployees,
  contracts as seedContracts,
  chargeCodes as seedChargeCodes,
  laborCategories,
  seedTimeEntries,
  seedSubmissions,
} from './timesheetMockData'
import * as ts_sb from './timesheetSupabaseService'
import { timesheetCatsService } from './timesheetCatsService'
import { resolveChargeLineBillable } from '../lib/billabilityAdapter'
// Single source of truth for the SAP-backend switch (was a local VITE_TS_BACKEND read).
import { IS_LIVE_SAP } from '@/config/sapBackend'
import { getPtoObjectCodes, isPtoObjectId, setPtoObjectCodes } from '../lib/ptoObjects'
import { primeHolidayOverlay } from './sapCalendarService'

export {
  DEMO_EMPLOYEE_ID,
  DEMO_SUPERVISOR_ID,
  PTO_CHARGE_CODE_ID,
  laborCategories,
  getMonday,
  addDays,
  toIsoDate,
} from './timesheetMockData'

// Re-export the seeds for callers that genuinely want the demo data (not the
// runtime cache). Live components should prefer the service getters.
export {
  employees as seedEmployees,
  contracts as seedContracts,
  chargeCodes as seedChargeCodes,
} from './timesheetMockData'

// ─── Data-source resolution (mirrors services/dataService.ts) ───────────────

type DataSource = 'mock' | 'api' | 'supabase'

function resolveConfiguredSource(): DataSource {
  const explicit = import.meta.env.VITE_DATA_SOURCE as string | undefined
  if (explicit === 'supabase') return 'supabase'
  if (explicit === 'api') return 'api'
  if (explicit === 'mock') return 'mock'
  if (import.meta.env.VITE_MOCK_DATA === 'false') return 'api'
  return 'mock'
}

const CONFIGURED_SOURCE = resolveConfiguredSource()

function isDemoMode(): boolean {
  try {
    const mode = localStorage.getItem('ppm-app-mode')
    return mode === null || mode === 'demo'
  } catch { return false }
}

function resolveRuntimeSource(): DataSource {
  // Demo mode respects the configured source (defaults to mock JSON).
  // Non-demo modes (sandbox/dev/quality/prod) always go to Supabase.
  if (isDemoMode()) return CONFIGURED_SOURCE
  return CONFIGURED_SOURCE === 'mock' ? 'supabase' : CONFIGURED_SOURCE
}

function shouldUseSupabase(): boolean {
  return resolveRuntimeSource() === 'supabase'
}

// The S/4-hosted CATS build (Phase 3). When set, reference data is primed from
// the read-only ZUI_M12_* OData services instead of Supabase. Dormant (flag
// unset) for the live Vercel/Supabase build, so its behavior is unchanged.
// Read at call time: the hosted app switches onto CATS at startup
// (enableHostedSapTimesheet), after this module may already be loaded.
const isCatsBackend = (): boolean => IS_LIVE_SAP

// ─── Runtime reference-data cache ────────────────────────────────────────────
// Sync getters (used inside React render code) read from these arrays.
// `bootstrapReferenceData()` populates them from Supabase in supabase mode,
// or leaves the seeds in place in mock mode.
const refData = {
  employees: [...seedEmployees],
  contracts: [...seedContracts],
  // The CATS build offers only real SAP charge objects. The seed list carries
  // demo codes (`ts-cc-pto`, `ts-cc-001`...) that CATS rejects as unknown WBS.
  chargeCodes: IS_LIVE_SAP ? [] : [...seedChargeCodes],
}
let referenceDataLoaded = false

// Synced SAP charge objects surfaced as timesheet charge codes (#4). `active` is
// the set selectable for NEW entries; `resolver` carries ALL of them (incl.
// inactive) so a historical entry against an object that later went inactive
// still resolves its label and stays editable.
let sapChargeCodesActive: TsChargeCode[] = []
const sapChargeCodeResolver = new Map<string, TsChargeCode>()

/** (Re)load SAP charge objects into the charge-code cache. Best-effort.
 *
 * Both builds resolve charge objects through the SAME policy-aware path
 * (`fetchChargeObjectCodes`): it reads objects (LIVE from PRPS/AUFK in the cats
 * build, Supabase cache in the live build) and applies the charge-object
 * POLICIES, so the picker's selectable ('active') set exactly matches the Charge
 * Objects admin screen. Previously the cats build used a raw WBS read that
 * ignored policies, so curation on the Charge Objects screen never reached the
 * timesheet picker. `all` (incl. policy-gated) still populates the resolver so
 * historical entries keep resolving their label. */
export async function reloadSapChargeObjectCodes(): Promise<void> {
  if (!isCatsBackend() && !shouldUseSupabase()) return
  try {
    const { useAppStore } = await import('@/store/appStore')
    const orgId = useAppStore.getState().currentUser?.organizationId ?? 'org_demo'
    const { fetchChargeObjectCodes } = await import('./chargeObjectService')
    const sets = await fetchChargeObjectCodes(orgId)
    sapChargeCodesActive = sets.active
    sapChargeCodeResolver.clear()
    for (const c of sets.all) sapChargeCodeResolver.set(c.id, c)
  } catch (err) {
    console.warn('[timesheet] SAP charge-object codes load failed', err)
  }
}

/**
 * Merge additional SAP charge objects into the charge-code cache (resolver +
 * active picker list), deduped by id. The CATS adapter calls this on every week
 * read with the charge objects the week's entries reference, so an entry always
 * resolves to a grid line (getChargeCode) and its WBS/order shows in the picker,
 * even when the WBS-structure catalog does not carry it. Additive; never removes.
 */
export function mergeSapChargeObjectCodes(codes: readonly TsChargeCode[]): void {
  for (const c of codes) {
    if (!sapChargeCodeResolver.has(c.id)) {
      sapChargeCodeResolver.set(c.id, c)
      sapChargeCodesActive.push(c)
    }
  }
}

// Dedupes concurrent callers (the shell's usePersonaReconcile and the timesheet
// routes provider can both request the bootstrap in the same mount cycle).
let referenceDataInflight: Promise<void> | null = null

export function bootstrapReferenceData(force = false): Promise<void> {
  if (referenceDataLoaded && !force) return Promise.resolve()
  if (referenceDataInflight && !force) return referenceDataInflight
  const run = doBootstrapReferenceData()
  referenceDataInflight = run
  const clear = () => { if (referenceDataInflight === run) referenceDataInflight = null }
  run.then(clear, clear)
  return run
}

async function doBootstrapReferenceData(): Promise<void> {
  // CATS build (Phase 3): prime the same refData cache from OData so the sync
  // getters keep returning without a network hop. Employees merge onto the seed
  // roster (the rich seed personas are preserved); charge objects come from the
  // ZUI_M12_* read services. Best-effort: any failure keeps the seeds.
  if (isCatsBackend()) {
    try {
      const { loadCatsEmployees } = await import('./catsReferenceData')
      const emp = await loadCatsEmployees(refData.employees)
      if (emp.length > 0) { refData.employees.length = 0; refData.employees.push(...emp) }
    } catch (err) {
      console.warn('[timesheet] CATS employees load failed, keeping seeds', err)
    }
    await reloadSapChargeObjectCodes()
    // PTO / leave config (category -> AWART, cost center -> PTO WBS). Best-effort;
    // the resolver falls back to the H-PTO<cc> convention + built-in AWART map.
    try {
      const { loadPtoConfig } = await import('./ptoConfigService')
      await loadPtoConfig('sap')
    } catch (err) {
      console.warn('[timesheet] PTO config load failed, using fallback', err)
    }
    // Overlay the customer's SAP holiday calendar onto the built-in federal list.
    try {
      await primeHolidayOverlay('sap')
    } catch (err) {
      console.warn('[timesheet] SAP holiday overlay load failed, using federal calendar', err)
    }
    stampSapWorkerCountries()
    referenceDataLoaded = true
    return
  }
  if (!shouldUseSupabase()) {
    referenceDataLoaded = true
    return
  }
  const [emp, con, cc] = await Promise.all([
    ts_sb.fetchEmployees().catch((err) => { console.warn('[timesheet] fetchEmployees failed, keeping seeds', err); return [] }),
    ts_sb.fetchContracts().catch((err) => { console.warn('[timesheet] fetchContracts failed, keeping seeds', err); return [] }),
    ts_sb.fetchChargeCodes().catch((err) => { console.warn('[timesheet] fetchChargeCodes failed, keeping seeds', err); return [] }),
  ])
  if (emp.length > 0) { refData.employees.length = 0; refData.employees.push(...emp) }
  if (con.length > 0) { refData.contracts.length = 0; refData.contracts.push(...con) }
  if (cc.length > 0) { refData.chargeCodes.length = 0; refData.chargeCodes.push(...cc) }
  await reloadSapChargeObjectCodes()
  stampSapWorkerCountries()
  referenceDataLoaded = true
}

// Worker country from the SAP HR mini master (personnel area / MOLGA), stamped
// onto the roster in the background so start-up never waits on SAP. It selects
// the UK / India labor-law rules; until it lands every worker is treated as US.
function stampSapWorkerCountries(): void {
  void import('./sapWorkerCountryService')
    .then(({ withSapWorkerCountries }) => withSapWorkerCountries(refData.employees))
    .then((stamped) => {
      if (stamped.length === refData.employees.length) {
        refData.employees.length = 0
        refData.employees.push(...stamped)
      }
    })
    .catch((err) => console.warn('[timesheet] SAP HR worker country read failed; workers treated as US', err))
}

/**
 * The hosted app has just switched onto SAP CATS (enableHostedSapTimesheet).
 * Drop the demo charge codes, which CATS rejects as unknown WBS, and force the
 * next bootstrap to prime from SAP instead of the app database.
 */
export function resetReferenceDataForSap(): void {
  refData.chargeCodes.length = 0
  referenceDataLoaded = false
  referenceDataInflight = null
}

export function isReferenceDataLoaded(): boolean {
  return referenceDataLoaded
}

// ─── In-memory mock store (time entries + submissions) ───────────────────────

let _entries: TsTimeEntry[] = [...seedTimeEntries]
const _submissions: TsSubmission[] = [...seedSubmissions]

function mockGetEntriesForWeek(employeeId: string, weekStart: string): TsTimeEntry[] {
  const start = new Date(weekStart)
  const end = new Date(start)
  end.setDate(end.getDate() + 7)
  return _entries.filter((e) => {
    if (e.employeeId !== employeeId) return false
    const d = new Date(e.date)
    return d >= start && d < end
  })
}

function mockUpsertEntry(entry: TsTimeEntry): void {
  const idx = _entries.findIndex((e) => e.id === entry.id)
  if (idx >= 0) _entries[idx] = entry
  else _entries.push(entry)
}

function mockDeleteEntry(entryId: string): void {
  _entries = _entries.filter((e) => e.id !== entryId)
}

function mockGetSubmissions(employeeId?: string): TsSubmission[] {
  return employeeId ? _submissions.filter((s) => s.employeeId === employeeId) : _submissions
}

function mockGetPendingApprovals(supervisorId: string): TsSubmission[] {
  const reports = refData.employees.filter((e) => e.supervisorId === supervisorId).map((e) => e.id)
  return _submissions.filter((s) => reports.includes(s.employeeId) && s.status === 'pending')
}

function mockApproveSubmission(submissionId: string): void {
  const s = _submissions.find((s) => s.id === submissionId)
  if (s) s.status = 'approved'
}

function mockRejectSubmission(submissionId: string): void {
  const s = _submissions.find((s) => s.id === submissionId)
  if (s) s.status = 'rejected'
}

function mockSubmitWeek(employeeId: string, weekStart: string): TsSubmission {
  const entries = mockGetEntriesForWeek(employeeId, weekStart)
  const totalHours = entries.reduce((sum, e) => sum + e.hours, 0)
  const anomalyCount = entries.filter((e) => e.hours > 10).length
  const sub: TsSubmission = {
    id: `sub-${Date.now()}`,
    employeeId,
    weekStart,
    totalHours,
    status: 'pending',
    submittedAt: new Date().toISOString(),
    anomalyCount,
  }
  _submissions.push(sub)
  entries.forEach((e) => { e.submissionId = sub.id })
  return sub
}

/** Re-derive an entry's billable flag + customer from its charge line (the
 *  single source of truth for billability). No charge line found → unchanged. */
function stampEntryBillability(entry: TsTimeEntry): TsTimeEntry {
  const cc = refData.chargeCodes.find((c) => c.id === entry.chargeCodeId)
  if (!cc) return entry
  const contract = refData.contracts.find((c) => c.id === cc.contractId)
  const { isBillable, customerId } = resolveChargeLineBillable(cc, contract)
  return { ...entry, isBillable, customerId }
}

// ─── Backend selection (runbook Appendix C factory seam) ─────────────────────
// A single selector chooses the async data backend. The CATS branch is dormant
// unless VITE_TS_BACKEND=cats (the S/4-hosted build); with the flag unset (the
// live default) selection collapses to the exact supabase-vs-mock decision the
// inline shouldUseSupabase() checks made before, so behavior is unchanged.
//
// The sync getters on the facade below are NOT part of this contract: they read
// the shared `refData` cache (primed by bootstrapReferenceData) and stay
// backend-agnostic. Phase 3 re-points bootstrapReferenceData at OData so those
// getters keep working unchanged.

type ApprovedLeave = { startDate: string; endDate: string; ptoChargeCodeId: string; reason?: string }

/**
 * The frozen async surface every backend implements (mock / supabase / CATS).
 * The Phase 3 CATS adapter (timesheetCatsService) conforms to this method-for-
 * method so the UI never changes.
 * See docs/architecture/handoffs/timesheet-service-interface.md.
 */
export interface TimesheetBackend {
  updateEmployee(
    employeeId: string,
    patch: Partial<Pick<TsEmployee, 'payClass' | 'scheduleType' | 'weekStartDay'>>,
  ): Promise<void>
  getApprovedLeavesAsync(employeeId: string): Promise<ApprovedLeave[]>
  getEntriesForWeek(employeeId: string, weekStart: string): Promise<TsTimeEntry[]>
  upsertEntry(entry: TsTimeEntry): Promise<void>
  /** `reason` is the required change-explanation when clearing already-approved
   *  time — recorded as the CATS change record's ShortText (audit trail). */
  deleteEntry(entryId: string, reason?: string): Promise<void>
  getSubmissions(employeeId?: string): Promise<TsSubmission[]>
  getAllSubmissions(): Promise<TsSubmission[]>
  getSubmissionById(id: string): Promise<TsSubmission | null>
  getSubmissionHistory(employeeId: string, weekStart: string): Promise<TsWorkflowEvent[]>
  getHistoryForEntries(entryIds: string[]): Promise<ts_sb.TsEntryHistoryRow[]>
  getPendingApprovals(supervisorId: string): Promise<TsSubmission[]>
  approveSubmission(submissionId: string, actorId?: string | null): Promise<void>
  rejectSubmission(submissionId: string, actorId?: string | null, reasonCode?: string | null): Promise<void>
  submitWeek(employeeId: string, weekStart: string, orgId?: string): Promise<TsSubmission>
  getPendingApprovalSegments(approverId: string, orgId?: string): Promise<ts_sb.PendingApprovalSegment[]>
  decideApprovalSegment(
    segmentId: string,
    decision: 'approved' | 'rejected',
    actorId?: string | null,
    reasonCode?: string | null,
    orgId?: string,
  ): Promise<void>
}

/**
 * Approved-leave fixtures (mock/demo). Extracted so the sync getter and the mock
 * backend's async variant resolve identical data.
 */
function readApprovedLeaves(employeeId: string): ApprovedLeave[] {
  const fixtures: Record<string, ApprovedLeave[]> = {
    // Morgan took a long weekend off Memorial Day week + a future trip
    'ts-emp-004': [
      { startDate: '2026-05-25', endDate: '2026-05-27', ptoChargeCodeId: 'ts-cc-pto', reason: 'Memorial Day extended weekend' },
      { startDate: '2026-06-15', endDate: '2026-06-19', ptoChargeCodeId: 'ts-cc-pto', reason: 'Family trip' },
    ],
  }
  // Try direct id, then resolve via pernr (mock uses ts-emp-XXX; supabase uses
  // user_XXX with ts-emp-XXX as pernr).
  if (fixtures[employeeId]) return fixtures[employeeId]!
  const byPernr = refData.employees.find((e) => e.id === employeeId)?.pernr
  if (byPernr && fixtures[byPernr]) return fixtures[byPernr]!
  return []
}

// In-module mock backend (was the `else` side of each shouldUseSupabase() check).
const timesheetMockBackend: TimesheetBackend = {
  async updateEmployee() { /* mock: cache-only; the facade already applied the patch */ },
  async getApprovedLeavesAsync(employeeId) { return readApprovedLeaves(employeeId) },
  async getEntriesForWeek(employeeId, weekStart) { return mockGetEntriesForWeek(employeeId, weekStart) },
  async upsertEntry(entry) { mockUpsertEntry(entry) },
  async deleteEntry(entryId) { mockDeleteEntry(entryId) },
  async getSubmissions(employeeId) { return mockGetSubmissions(employeeId) },
  async getAllSubmissions() { return mockGetSubmissions() },
  async getSubmissionById(id) { return mockGetSubmissions().find((s) => s.id === id) ?? null },
  async getSubmissionHistory() { return [] },
  async getHistoryForEntries() { return [] }, // mock mode has no history shadow
  async getPendingApprovals(supervisorId) { return mockGetPendingApprovals(supervisorId) },
  async approveSubmission(submissionId) { mockApproveSubmission(submissionId) },
  async rejectSubmission(submissionId) { mockRejectSubmission(submissionId) },
  async submitWeek(employeeId, weekStart) { return mockSubmitWeek(employeeId, weekStart) },
  async getPendingApprovalSegments() { return [] },
  async decideApprovalSegment() { /* mock: no-op (segmented queue is a supabase-mode feature) */ },
}

// Supabase backend (was the `if (shouldUseSupabase())` side of each check).
const timesheetSupabaseBackend: TimesheetBackend = {
  async updateEmployee(employeeId, patch) {
    try {
      await ts_sb.updateEmployee(employeeId, patch)
    } catch (e) {
      // Anon/RLS-denied write in the demo — the facade already applied the
      // change to the in-memory cache for the session. Warn, don't fail.

      console.warn(`[updateEmployee] DB write skipped for ${employeeId} (applied locally only):`, e)
    }
  },
  async getApprovedLeavesAsync(employeeId) { return ts_sb.fetchApprovedLeaves(employeeId) },
  async getEntriesForWeek(employeeId, weekStart) { return ts_sb.getEntriesForWeek(employeeId, weekStart) },
  async upsertEntry(entry) { await ts_sb.upsertEntry(entry) },
  async deleteEntry(entryId) { await ts_sb.deleteEntry(entryId) },
  async getSubmissions(employeeId) { return ts_sb.getSubmissions(employeeId) },
  async getAllSubmissions() { return ts_sb.getSubmissions() },
  async getSubmissionById(id) { return ts_sb.getSubmissionById(id) },
  async getSubmissionHistory(employeeId, weekStart) { return ts_sb.getSubmissionHistory(employeeId, weekStart) },
  async getHistoryForEntries(entryIds) { return ts_sb.getHistoryForEntries(entryIds) },
  async getPendingApprovals(supervisorId) { return ts_sb.getPendingApprovals(supervisorId) },
  async approveSubmission(submissionId, actorId) { await ts_sb.approveSubmission(submissionId, actorId ?? null) },
  async rejectSubmission(submissionId, actorId, reasonCode) { await ts_sb.rejectSubmission(submissionId, actorId ?? null, reasonCode ?? null) },
  async submitWeek(employeeId, weekStart, orgId) { return ts_sb.submitWeek(employeeId, weekStart, orgId) },
  async getPendingApprovalSegments(approverId, orgId) { return ts_sb.getPendingApprovalSegments(approverId, orgId) },
  async decideApprovalSegment(segmentId, decision, actorId, reasonCode, orgId) {
    await ts_sb.decideApprovalSegment(segmentId, decision, actorId ?? null, reasonCode ?? null, orgId)
  },
}

/**
 * The backend factory (runbook Appendix C). One selector; the CATS branch is
 * dormant unless VITE_TS_BACKEND=cats. With the flag unset (the live default)
 * this is exactly the prior supabase-vs-mock dispatch.
 */
function getTimesheetService(): TimesheetBackend {
  if (isCatsBackend()) return timesheetCatsService  // Phase 3 (CATS-backed); stub in Phase 1
  if (shouldUseSupabase()) return timesheetSupabaseBackend                     // unchanged
  return timesheetMockBackend
}

// ─── Public service facade (delegates async work through the factory) ─────────

export const timesheetDataService = {
  // Synchronous reads from the runtime reference-data cache. Mock-mode keeps
  // the seeds; supabase-mode replaces them via bootstrapReferenceData().
  getEmployee(id: string): TsEmployee | undefined {
    // ProjectTasks #81 — pernr-based fallback. In mock mode the demo IDs
    // ('ts-emp-001' / 'ts-emp-002' / ...) are the row IDs directly. In
    // supabase mode those same strings live in the `pernr` column (the
    // row IDs are `user_001` / `user_002` / ... — different schema from
    // ts_employees). Migration 00131 sets pernr on public.users for the
    // five anchor personas, and fetchEmployees now carries pernr through
    // the mapper, so this fallback resolves both shapes from one call.
    const hit = refData.employees.find((e) => e.id === id)
        ?? refData.employees.find((e) => e.pernr === id)
    if (hit || !/^\d+$/.test(id)) return hit
    // On CATS a personnel number comes back unpadded from some services
    // ("65" on an approval segment) and padded from others ("00000065" in
    // the roster), so compare numbers without their leading zeros.
    const bare = id.replace(/^0+/, '')
    return refData.employees.find((e) => /^\d+$/.test(e.id) && e.id.replace(/^0+/, '') === bare)
  },
  getEmployees(): TsEmployee[] {
    return refData.employees
  },
  /**
   * Admin-only patch of a small whitelist of employee fields. Persists to
   * Supabase in supabase mode, and always mutates the in-memory ref so
   * consumers (Dashboard / MyTimesheet / Sidebar) pick up the change on
   * the next render without re-bootstrapping. Today only the fields
   * exposed by the Settings page editor are accepted — adding new ones
   * here is the easy/safe place since the supabase-side mapping lives
   * adjacent (see ts_sb.updateEmployee).
   */
  async updateEmployee(
    employeeId: string,
    patch: Partial<Pick<TsEmployee, 'payClass' | 'scheduleType' | 'weekStartDay'>>,
  ): Promise<void> {
    // Apply to the in-memory reference cache FIRST so the change takes effect
    // for the session regardless of the DB write. The deployed demo is anon and
    // public.users is RLS-protected, so the Supabase write can be denied — but
    // the setting (e.g. weekStartDay) must still drive the grid's week-start
    // alignment + cell coloring. getEmployee() reads this cache synchronously.
    const emp = refData.employees.find((e) => e.id === employeeId) ?? refData.employees.find((e) => e.pernr === employeeId)
    if (emp) Object.assign(emp, patch)
    // Persist through the selected backend (mock: no-op; supabase: RLS-tolerant
    // write; cats: Phase 3). The cache update above already took effect for the
    // session regardless of the backend outcome.
    await getTimesheetService().updateEmployee(employeeId, patch)
  },
  /**
   * Stream H — approved leaves. Mock mode returns hand-seeded fixtures
   * keyed on the mock id OR pernr (so it resolves the same employee in
   * both id schemas). Supabase mode goes through `getApprovedLeavesAsync`
   * — the banner uses that via useQuery. Provisional until pto_requests
   * exposes a status='approved' filter.
   */
  getApprovedLeaves(employeeId: string): { startDate: string; endDate: string; ptoChargeCodeId: string; reason?: string }[] {
    return readApprovedLeaves(employeeId)
  },
  /**
   * Async variant that dispatches to Supabase when mode=supabase. The
   * VacationCoverBanner consumes this via useQuery so demo + production
   * data both flow through one path.
   */
  async getApprovedLeavesAsync(employeeId: string): Promise<{ startDate: string; endDate: string; ptoChargeCodeId: string; reason?: string }[]> {
    return getTimesheetService().getApprovedLeavesAsync(employeeId)
  },
  getContracts(): TsContract[] {
    return refData.contracts
  },
  /**
   * CATS build: load the PTO objects for this employee (by PERNR) and make them
   * the PTO entries in the picker. Reloaded when the employee changes, which
   * includes impersonation. Returns [] outside the CATS build.
   */
  async loadPtoObjects(pernr: string | null | undefined): Promise<TsChargeCode[]> {
    if (!isCatsBackend()) return []
    const { loadPtoObjectCodes } = await import('./catsReferenceData')
    const codes = await loadPtoObjectCodes(pernr)
    setPtoObjectCodes(codes)
    return codes
  },
  getChargeCodes(contractId?: string): TsChargeCode[] {
    if (contractId) return refData.chargeCodes.filter((cc) => cc.contractId === contractId)
    // CATS build: the employee's PTO objects lead, labeled as PTO, and replace the
    // same WBS in the catalog so it is listed once.
    const pto = isCatsBackend() ? getPtoObjectCodes() : []
    const sapActive = pto.length > 0
      ? sapChargeCodesActive.filter((cc) => !isPtoObjectId(cc.id))
      : sapChargeCodesActive
    // Active synced SAP charge objects (#4) are selectable alongside charge lines.
    return pto.length > 0 || sapActive.length > 0
      ? [...pto, ...refData.chargeCodes, ...sapActive]
      : refData.chargeCodes
  },
  getChargeCode(id: string): TsChargeCode | undefined {
    if (isCatsBackend() && isPtoObjectId(id)) {
      return getPtoObjectCodes().find((cc) => cc.id === id)
    }
    // Resolve against the live reference set first (Supabase charge_lines after
    // bootstrap, seed before). Fall back to the bundled seed for ids the live
    // set doesn't carry — e.g. a handful of legacy `ts-cc-*` time entries that
    // predate the charge_lines model. Strictly additive: only affects ids that
    // would otherwise resolve to undefined, so the labor export posts them
    // (with their seed WBS) instead of skipping.
    const hit = refData.chargeCodes.find((cc) => cc.id === id)
      // Synced SAP charge objects (incl. inactive ones) so historical entries
      // against a now-inactive WBS still resolve their label (#4/#5).
      ?? sapChargeCodeResolver.get(id)
      ?? (isCatsBackend() ? undefined : seedChargeCodes.find((cc) => cc.id === id))
    if (hit) return hit
    // Fallback for a synthetic SAP charge-object id (`<type>:<key>`) that no
    // catalog carries yet — e.g. a CATS record posted against a WBS the
    // WBS-structure service does not return. Synthesize a minimal charge code so
    // the entry still renders as a grid line (labeled with its WBS/order/cost
    // center) instead of being dropped and only counted in the daily total
    // ("disappearing"). Only triggers for `type:key` ids (the CATS build); live
    // Supabase charge-line ids have no ':' so this never fires there.
    const sep = id.indexOf(':')
    if (sep > 0) {
      const key = id.slice(sep + 1)
      const isCc = id.startsWith('cost_center:')
      return {
        id,
        contractId: '',
        wbs: isCc ? '' : key,
        description: key,
        laborCategories: [],
        allowable: true,
        pool: null,
        costCenter: isCc ? key : null,
        billable: null,
      }
    }
    return undefined
  },
  getContract(id: string): TsContract | undefined {
    return refData.contracts.find((c) => c.id === id)
  },
  getLaborCategories(): TsLaborCategory[] {
    return laborCategories
  },

  // Async — mode-dispatched.
  async getEntriesForWeek(employeeId: string, weekStart: string): Promise<TsTimeEntry[]> {
    return getTimesheetService().getEntriesForWeek(employeeId, weekStart)
  },

  async upsertEntry(entry: TsTimeEntry): Promise<void> {
    // Billability is a property of the CHARGE LINE, not a per-entry user choice.
    // Stamp it (and the customer) from the charge line on every write so the
    // worker never sets it and revenue/utilization reporting stays correct.
    const billed = stampEntryBillability(entry)
    // Original input date — stamp the recorded-at timestamp the first time an
    // entry is written and preserve it forever after. Supabase rows get it from
    // the DB `created_at` default (so we don't send it there — that would
    // clobber the original on edit); mock-mode rows have no DB, so we stamp here
    // and persist it in the in-memory store. Late-entry detection reads this.
    const stamped = billed.createdAt ? billed : { ...billed, createdAt: new Date().toISOString() }
    await getTimesheetService().upsertEntry(stamped)
  },

  async deleteEntry(entryId: string, reason?: string): Promise<void> {
    await getTimesheetService().deleteEntry(entryId, reason)
  },

  async getSubmissions(employeeId?: string): Promise<TsSubmission[]> {
    return getTimesheetService().getSubmissions(employeeId)
  },

  /** All submissions across all employees — auditor / floor-check view. */
  async getAllSubmissions(): Promise<TsSubmission[]> {
    return getTimesheetService().getAllSubmissions()
  },

  /** Single-submission lookup for the Floor Check detail page. */
  async getSubmissionById(id: string): Promise<TsSubmission | null> {
    return getTimesheetService().getSubmissionById(id)
  },

  /**
   * Approval-workflow history for a week's submission — powers the inline
   * "Submission status" tracker on My Timesheet. Mock mode has no workflow
   * shadow, so the tracker falls back to status + submittedAt there.
   */
  async getSubmissionHistory(employeeId: string, weekStart: string): Promise<TsWorkflowEvent[]> {
    return getTimesheetService().getSubmissionHistory(employeeId, weekStart)
  },

  /** Audit history rows for an entry set — Floor Check timeline. */
  async getHistoryForEntries(entryIds: string[]) {
    return getTimesheetService().getHistoryForEntries(entryIds)
  },

  async getPendingApprovals(supervisorId: string): Promise<TsSubmission[]> {
    return getTimesheetService().getPendingApprovals(supervisorId)
  },

  async approveSubmission(submissionId: string, actorId?: string | null): Promise<void> {
    await getTimesheetService().approveSubmission(submissionId, actorId)
  },

  async rejectSubmission(submissionId: string, actorId?: string | null, reasonCode?: string | null): Promise<void> {
    await getTimesheetService().rejectSubmission(submissionId, actorId, reasonCode)
  },

  async submitWeek(employeeId: string, weekStart: string, orgId?: string): Promise<TsSubmission> {
    return getTimesheetService().submitWeek(employeeId, weekStart, orgId)
  },

  // ── Charge-object approval segments (Time Approval Routing, Part 1) ──────────
  // Supabase-only; mock mode returns empty / no-ops (the segmented queue is a
  // supabase-mode feature and the demo runs on supabase).
  async getPendingApprovalSegments(approverId: string, orgId?: string): Promise<ts_sb.PendingApprovalSegment[]> {
    return getTimesheetService().getPendingApprovalSegments(approverId, orgId)
  },

  async decideApprovalSegment(
    segmentId: string, decision: 'approved' | 'rejected',
    actorId?: string | null, reasonCode?: string | null, orgId?: string,
  ): Promise<void> {
    await getTimesheetService().decideApprovalSegment(segmentId, decision, actorId, reasonCode, orgId)
  },
}
